import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { imageUrl } from '../lib/images';

export default function Soefragt() {
  return (
    <>
      <SEO
        title="Containerfragt - både LCL og FCL - Kontakt os for tilbud"
        description="Containerfragt i det meste af verden. Fastlane tilbyder containerfragt i flere containertyper. Ønsker du multimodal transport? Ring nu for komplet tilbud."
        keywords="Søfragt, containerfragt, fragt Kina, fragt Indien, fragt til Indien, FCL, LCL"
        canonicalUrl="https://fastlane.dk/containerfragt-soefragt"
      />

      <PageHeader
        title="Søfragt / Containerfragt"
        subtitle="International containerfragt shortsea og oversea – både LCL stykgods og hele containere (FCL)."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Søfragt / Containerfragt' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Intro text */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <p>
              Fastlane ApS tilbyder international containerfragt af gods på skib både shortsea og oversea. Ved fragt i hele container bliver der typisk brugt 20 eller 40 fods containertyper, som er isoleret (dry cube) eller 40 fods high cube container.
            </p>
          </div>

          {/* Image from scrape */}
          <div className="w-fit max-w-full rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100">
            <img
              src={imageUrl('https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/137y0293-2-0.jpg', 'width=1200,quality=85,format=auto')}
              alt="Sib med containere og løstrailere shortsea"
              className="max-h-[350px] w-auto max-w-full object-cover block"
            />
            <p className="w-full text-xs text-slate-500 p-3 bg-slate-50 border-t border-slate-100 text-left">
              Skib med containere og løstrailere shortsea
            </p>
          </div>

          {/* LCL vs FCL */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
            <h2 className="text-2xl font-bold text-[#0f2b48]">
              Containerfragt som stykgods (LCL) eller i hele containere (FCL)
            </h2>
            <p>
              Når man vil sende gods med containerfragt kan man vælge at fragte som{' '}
              <Link to="/lcl-less-container-load" className="text-sky-700 font-semibold underline">
                stykgods (LCL = less than container load)
              </Link>
              , hvor man køber en plads i en container, hvor der også er andet gods med.
            </p>
            <p>
              Ønsker man at fragte større godsmængder, sendes det i en eller flere hele containere (FCL = full container load).
            </p>
            <p>
              Ønsker du containerkørsel til/fra havn til lokalitet, kan du læse mere om containerkørsel og fortoldning af gods{' '}
              <Link to="/koersel-af-importcontainere" className="text-sky-700 font-semibold underline">
                her
              </Link>
              .
            </p>

            <div className="pt-2 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Link
                to="/lcl-less-container-load"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-sky-50 text-sky-800 text-xs font-semibold hover:bg-sky-100 border border-sky-200 text-center"
              >
                Læs mere om forsendelse af stykgods / LCL
              </Link>
              <Link
                to="/kontakt"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-[#b25d19] text-white text-xs font-semibold hover:bg-[#974d12] text-center"
              >
                Få fragt med container
              </Link>
            </div>
          </div>

          {/* Containertyper */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h2 className="text-xl font-bold text-[#0f2b48]">Containertyper</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              Der findes flere forskellige containertyper. Oftest bliver der brugt 20 fods eller 40 fods isoleret (dry cube) container eller 40 fods high cube containere. Du kan se mere om de forskellige container typer{' '}
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-8-rev-00-instruktion-containertyper-0.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-700 font-semibold underline"
              >
                her (PDF)
              </a>
              . Vi tilråder, at der altid tegnes{' '}
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/instruktion-forsikring-rev-00-1.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-700 font-semibold underline"
              >
                fragtforsikring (PDF)
              </a>{' '}
              ved skibsfragt.
            </p>
          </div>

          {/* Pris på fragt af container */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Pris på fragt af container</h2>
            <p>
              For at kunne tilbyde den mest præcise pris på, hvad det koster at sende en container er det vigtigt, vi kender afsenderadressen og modtageradressen, så containerfragten er klart defineret og den optimale rute/skibsforbindelse kan vælges. Ruten vælges ud fra pris og transittid så det matcher behovet bedst muligt.
            </p>
            <p>
              For den mest optimale fragtrate på containerfragten opnås, er det vigtigt som minimum at kende timingen for transporten, behovet, godstypen, godsværdien samt leveringsbetingelserne mellem afsender og køber eller sælger og modtager (ref. Incoterms 2020 - klik evt.{' '}
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/incoterms-2020-fastlane-5.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-700 font-semibold underline"
              >
                Incoterms PDF
              </a>
              ).
            </p>
          </div>

          {/* Container transport i det meste af verden */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Container transport i det meste af verden</h2>
            <p>
              Søfragt med containere foregår typisk mellem 2 eller flere havne. Det er derfor normalt, at der også er lokal kørsel i begge ender fra afsender til havn og fra havn til modtager. Ønsker du en komplet løsning fra dør til dør, så tilbyder Fastlane fragtrater, som inkluderer alle omkostninger på nær importtold og importmoms.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                <h3 className="font-bold text-[#0f2b48] text-sm mb-2">Disse er nogle af de lande, som vi betjener:</h3>
                <p className="text-xs text-slate-700 leading-relaxed">
                  <Link to="/containerfragt-sydafrika" className="text-sky-700 font-semibold underline">Sydafrika</Link>, Kina, Indien, Pakistan, Sydkorea, Thailand, Vietnam,{' '}
                  <Link to="/soefragt-usa-canada" className="text-sky-700 font-semibold underline">USA</Link>,{' '}
                  <Link to="/soefragt-usa-canada" className="text-sky-700 font-semibold underline">Canada</Link>, Brasilien, Chile, Argentina, Japan, Malaysia, Indonesien, Danmark, Norge, Sverige og England.
                </p>
              </div>

              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                <h3 className="font-bold text-[#0f2b48] text-sm mb-2">Nogle af de havnebyer vi sender via:</h3>
                <p className="text-xs text-slate-700 leading-relaxed">
                  Hamburg, Bremerhaven, Antwerpen, Rotterdam, Southampton, Shanghai, Ningbo, Hong Kong, Shenzhen, Guangzhou, Miami, New York, Toronto, Mumbai, Busan, Singapore, Karachi, Göteborg, Durban, Johannesburg, Cape Town og Port Elizabeth.
                </p>
              </div>
            </div>
          </div>

          {/* Betaling & Multimodal links */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Betaling på transport af container</h2>
            <p>
              Betalingsmåderne modtager betaler (freight collect) eller afsender betaler (prepaid) er hyppigt brugt. For at kunne vurdere den reelle omkostning til fragten er lidt baggrundsviden ikke af vejen. Målsætningen er at opnå en god kvalitet i transporterne samtidig med en god fragtpris. Læs mere{' '}
              <Link to="/import" className="text-sky-700 font-semibold underline">
                her
              </Link>
              .
            </p>

            <h3 className="text-xl font-bold text-[#0f2b48] pt-4">Multimodal Transport</h3>
            <p>
              Ønsker du løsning med{' '}
              <Link to="/fragt-kombineret-transport" className="text-sky-700 font-semibold underline">
                multimodal transport
              </Link>
              , tilbyder Fastlane også godstransport med lastbil, tog og luftfragt. På den måde får du sammensat den transportløsning, der passer præcis til dit behov. Det betyder, at vi sammensætter, hvad du har brug for af transport med{' '}
              <Link to="/godstransport-i-europa" className="text-sky-700 font-semibold underline">
                lastbil
              </Link>
              , tog, sø og{' '}
              <Link to="/luftfragt-0" className="text-sky-700 font-semibold underline">
                luft
              </Link>
              .
            </p>

            <p className="pt-4">
              Link til ofte stillede{' '}
              <Link to="/spoergsmaal-svar-fragt" className="text-sky-700 font-semibold underline">
                spørgsmål
              </Link>
              .
            </p>
          </div>

          {/* CTA Box */}
          <div className="p-6 sm:p-8 bg-sky-50 rounded-2xl border border-sky-200 space-y-3">
            <h3 className="text-xl font-bold text-[#0f2b48]">
              Få et uforpligtende tilbud på containerfragt med Fastlane
            </h3>
            <p className="text-sm text-slate-700">
              Ønsker du en mere præcis containerrate på det du skal have fragtet, så er du velkommen til at sende os en mail på{' '}
              <a href="mailto:booking@fastlane.dk" className="font-semibold text-sky-700">booking@fastlane.dk</a> eller ringe til os på{' '}
              <a href="tel:+4576121230" className="font-semibold text-slate-900 whitespace-nowrap">+45 76 12 12 30</a>. Du kan også sende os en{' '}
              <Link to="/fragtrater" className="font-semibold text-sky-700 underline">forespørgsel</Link>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
